import { Link, Navigate } from 'react-router-dom'

import { Netzstreifen } from '../../components/Netzstreifen'
import {
  BackIcon,
  DiskIcon,
  GifIcon,
  GruppeIcon,
  PaletteIcon,
  PhotoIcon,
  SonneIcon,
  StandortIcon,
} from '../../components/Icons'
import { useMe } from '../../hooks/queries'
import { useTexte } from '../../texte'
import { Kategorien, type Kategorie } from './Kategorien'
import { ChatfarbenCard } from './ChatfarbenCard'
import { ListenfarbenCard } from './ListenfarbenCard'
import { DiensteCard } from './DiensteCard'
import { ErkennungCard } from './ErkennungCard'
import { GifCard } from './GifCard'
import { KartenfarbenCard } from './KartenfarbenCard'
import { KnopffarbeCard } from './KnopffarbeCard'
import { LimitsCard } from './LimitsCard'
import { MedienCard } from './MedienCard'
import { Personenliste } from './PeoplePage'
import { SpeicherCard } from './SpeicherCard'

/**
 * Die Administration — alles, was die **ganze Installation** betrifft (#39).
 *
 * Aus dem Issue: „eigene Verwaltungsseite - nur für Benutzer mit
 * Admin-Rechten." Vorher hing das alles unter den persönlichen Einstellungen
 * untereinander: das eigene Konto neben Grenzen, Farben und Diensten, die für
 * jeden gelten. Wer nur seine Schriftgröße ändern wollte, scrollte an der
 * Aufenthaltserkennung vorbei.
 *
 * **Sie hieß einen Tag lang „Verwaltung".** Aus dem Betrieb kam der Name
 * „Administration", und er gilt jetzt überall — in der Zeile, die hierher
 * führt, in der Kopfzeile und in der Adresse. Zwei Wörter für dieselbe Sache
 * laufen auseinander; die alte Adresse leitet weiter, damit sie nicht ins
 * Leere führt.
 *
 * **Die Weiterleitung ist Bequemlichkeit, keine Sperre.** Entschieden wird im
 * Server — `requireAdmin` in `SettingsController` und `PeopleController`, und
 * `/api/settings` liefert die Decke ohnehin nur der Verwaltung. Hier wird
 * niemand ferngehalten, hier wird nur niemandem eine Seite gezeigt, auf der
 * jede Karte leer bliebe.
 */
export function AdministrationPage() {
  const me = useMe()
  const t = useTexte()

  // Solange `me` nicht dasteht, wird nicht weitergeleitet: sonst flöge jeder
  // beim Neuladen kurz hinaus, weil die Antwort noch unterwegs ist.
  if (me.data !== undefined && !me.data.may_manage_people) {
    return <Navigate to="/settings" replace />
  }

  const kategorien: Kategorie[] = [
    {
      schluessel: 'personen',
      titel: t.administration.personen,
      symbol: <GruppeIcon size={19} />,
      inhalt: () => <Personenliste />,
    },
    {
      schluessel: 'speicher',
      titel: t.administration.speicherplatz,
      symbol: <DiskIcon size={19} />,
      inhalt: () => (
        <>
          <SpeicherCard />
          {/* Beides sieht auf denselben Bestand: was liegt da, und ist es
              überall abspielbar. */}
          <MedienCard />
        </>
      ),
    },
    {
      schluessel: 'grenzen',
      titel: t.administration.medien,
      symbol: <PhotoIcon size={19} />,
      inhalt: () => <LimitsCard />,
    },
    {
      schluessel: 'farben',
      titel: t.administration.farben,
      symbol: <PaletteIcon size={19} />,
      inhalt: () => (
        <>
          <ChatfarbenCard />
          <KartenfarbenCard />
          <ListenfarbenCard />
          <KnopffarbeCard />
        </>
      ),
    },
    {
      schluessel: 'erkennung',
      titel: t.administration.aufenthalte,
      symbol: <StandortIcon size={19} />,
      inhalt: () => <ErkennungCard />,
    },
    {
      schluessel: 'dienste',
      titel: t.administration.ortUndWetter,
      symbol: <SonneIcon size={19} />,
      inhalt: () => <DiensteCard />,
    },
    {
      schluessel: 'gifs',
      titel: t.administration.gifs,
      symbol: <GifIcon size={19} />,
      inhalt: () => <GifCard />,
    },
  ]

  return (
    <>
      <Netzstreifen />
      <Kategorien
        titel={t.administration.titel}
        zurueck={
          <Link className="icon-button" to="/settings" aria-label={t.einstellungen.zurueck}>
            <BackIcon />
          </Link>
        }
        kategorien={kategorien}
      />
    </>
  )
}
